/** #743 — unresolvable Qobuz stream-url skip-on-advance (extracted from QueueController). */

import { showImperativeToast } from '../providers/UIProvider';
import { useMaestroStore } from '../store/maestroStore';
import { showUserPlayRetryToast } from '../utils/playFailureUI';
import { logSkipConfirmedFailure } from './playUriConfirmation';

export const STREAM_UNAVAILABLE_MESSAGE =
  'This track isn\'t available to stream right now — it may be region-restricted by the label';

export const STREAM_RESOLUTION_TOAST_MS = 3000;
export const STREAM_RESOLUTION_SKIP_CAP = 5;

export type StreamResolutionFailureKind = 'client' | 'server' | 'unavailable';

const TTL_MS = 30_000;

type LastFailure = {
  status?: number;
  kind: StreamResolutionFailureKind;
  at: number;
};

let lastFailure: LastFailure | null = null;
let consecutiveUnresolvableSkips = 0;

export function isStreamResolutionServerError(status?: number): boolean {
  return typeof status === 'number' && status >= 500;
}

export function isStreamResolutionClientError(status?: number): boolean {
  return typeof status === 'number' && status >= 400 && status < 500;
}

export function recordStreamResolutionFailure(status: number | 'unavailable'): void {
  if (status === 'unavailable') {
    lastFailure = { kind: 'unavailable', at: Date.now() };
    return;
  }
  if (isStreamResolutionServerError(status)) {
    lastFailure = { status, kind: 'server', at: Date.now() };
    return;
  }
  if (isStreamResolutionClientError(status)) {
    lastFailure = { status, kind: 'client', at: Date.now() };
  }
}

export function consumeStreamResolutionFailure(): void {
  lastFailure = null;
}

export function streamResolutionFailureMessage(status?: number | null): string | null {
  if (status === undefined || status === null) return STREAM_UNAVAILABLE_MESSAGE;
  if (isStreamResolutionClientError(status) || isStreamResolutionServerError(status)) {
    return STREAM_UNAVAILABLE_MESSAGE;
  }
  return null;
}

export function streamResolutionStopToastMessage(): string {
  return `Couldn't play the next tracks — stopped after ${STREAM_RESOLUTION_SKIP_CAP} unavailable tracks in a row.`;
}

export function resetStreamResolutionSkipStateForTests(): void {
  lastFailure = null;
  consecutiveUnresolvableSkips = 0;
}

export function resetStreamResolutionSkipCounter(): void {
  consecutiveUnresolvableSkips = 0;
}

export function getStreamResolutionSkipCountForTests(): number {
  return consecutiveUnresolvableSkips;
}

/** Retry stream-url fetch once on 5xx (Bob Kravis geo-block case). */
export async function fetchStreamUrlWith5xxRetry(
  fetchOnce: () => Promise<Response>,
): Promise<Response> {
  let resp = await fetchOnce();
  if (isStreamResolutionServerError(resp.status)) {
    resp = await fetchOnce();
  }
  return resp;
}

/** Record 4xx/5xx from stream-url response; surface auth failures separately. */
export async function finalizeStreamUrlErrorResponse(
  resp: Response,
  trackId: string,
  formatId: number,
): Promise<null> {
  const errText = await resp.text().catch(() => '');
  if (
    resp.status !== 401
    && resp.status !== 403
    && (isStreamResolutionServerError(resp.status) || isStreamResolutionClientError(resp.status))
  ) {
    recordStreamResolutionFailure(resp.status);
  }
  if (resp.status === 401 || resp.status === 403) {
    const { surfaceStreamingAuthFailure } = await import('../lib/streamAuthFailure');
    surfaceStreamingAuthFailure(resp.status, errText);
  }
  const { diagnosticLog } = await import('../utils/diagnosticLogger');
  diagnosticLog(
    'error',
    'protocol',
    `Qobuz getFileUrl: failed — format ${formatId}`,
    {
      trackId,
      requestedFormat: formatId,
      actualFormat: undefined,
      statusCode: resp.status,
      mimeType: resp.headers.get('content-type') || undefined,
    },
  );
  return null;
}

export type StreamResolutionSkipContext = {
  playIntent: 'user' | 'queue-advance';
  priorPlayback: {
    currentTrack: unknown;
    currentStreamUrl: string | null;
  } | null;
  claimFailureRecovery: (owner: string) => boolean;
  releaseFailureRecovery: () => void;
  restorePlaybackSnapshot: (snap: StreamResolutionSkipContext['priorPlayback']) => void;
  playNextTrack: (track: unknown) => Promise<void>;
  clearPlayTrackInflight: () => void;
};

export async function handleStreamResolutionFailure(
  ctx: StreamResolutionSkipContext,
): Promise<void> {
  consumeStreamResolutionFailure();

  const store = useMaestroStore.getState();
  const trackTitle =
    store.queue[store.queueIndex]?.track?.title
    || store.currentTrack?.title
    || 'This track';

  if (ctx.playIntent === 'user') {
    if (!ctx.claimFailureRecovery('user-pause')) return;
    const renderer = store.activeConnectorRenderer;
    const zoneName = renderer?.name || store.discovery?.activeZone?.name || 'your device';
    showUserPlayRetryToast(
      trackTitle,
      zoneName,
      STREAM_UNAVAILABLE_MESSAGE,
      store.currentTrack,
      store.currentStreamFormatId,
      renderer?.id || store.discovery?.activeZone?.id,
    );
    ctx.releaseFailureRecovery();
    return;
  }

  if (!ctx.claimFailureRecovery('stream-resolution-skip')) return;

  consecutiveUnresolvableSkips++;

  if (consecutiveUnresolvableSkips >= STREAM_RESOLUTION_SKIP_CAP) {
    showImperativeToast(streamResolutionStopToastMessage(), STREAM_RESOLUTION_TOAST_MS);
    consecutiveUnresolvableSkips = 0;
    store.setPlaybackState('paused');
    store.setRendererHasStream(false);
    ctx.releaseFailureRecovery();
    return;
  }

  const nextIdx = store.queueIndex + 1;
  if (nextIdx < store.queue.length) {
    showImperativeToast(STREAM_UNAVAILABLE_MESSAGE, STREAM_RESOLUTION_TOAST_MS);
    const rendererName =
      store.activeConnectorRenderer?.name || store.discovery?.activeZone?.name || 'renderer';
    logSkipConfirmedFailure('stream_unavailable', rendererName);
    store.setQueueIndex(nextIdx);
    const nextItem = store.queue[nextIdx];
    if (nextItem?.track) {
      ctx.clearPlayTrackInflight();
      ctx.releaseFailureRecovery();
      await ctx.playNextTrack(nextItem.track);
      return;
    }
  }

  consecutiveUnresolvableSkips = 0;
  ctx.restorePlaybackSnapshot(ctx.priorPlayback);
  store.setPlaybackState('paused');
  ctx.releaseFailureRecovery();
}
